import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Building2, Loader2, RefreshCw } from "lucide-react";
import PunjabEmblem from "@/components/PunjabEmblem";

export default function Login() {
  const { login, verifyOtp } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("password");

  // Password flow
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [captchaId, setCaptchaId] = useState("");
  const [captchaQ, setCaptchaQ] = useState("");
  const [captchaA, setCaptchaA] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // OTP flow
  const [otpEmail, setOtpEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpDebug, setOtpDebug] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);

  const refreshCaptcha = async () => {
    const { data } = await api.get("/auth/captcha");
    setCaptchaId(data.id);
    setCaptchaQ(data.question);
    setCaptchaA("");
  };
  useEffect(() => { refreshCaptcha(); }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const user = await login({ email, password, captcha_id: captchaId, captcha_answer: captchaA });
      toast.success(`Welcome back, ${user.name}`);
      nav(user.role === "citizen" ? "/portal" : "/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      refreshCaptcha();
    } finally { setLoading(false); }
  };

  const requestOtp = async () => {
    setOtpLoading(true);
    try {
      const { data } = await api.post("/auth/otp/request", { email: otpEmail });
      setOtpSent(true);
      setOtpDebug(data.otp_debug || "");
      toast.success("OTP sent");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setOtpLoading(false);
  };

  const verifyOtpSubmit = async (e) => {
    e.preventDefault();
    setOtpLoading(true);
    try {
      const user = await verifyOtp({ email: otpEmail, otp });
      toast.success(`Welcome, ${user.name}`);
      nav(user.role === "citizen" ? "/portal" : "/dashboard");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setOtpLoading(false);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-slate-50 dark:bg-slate-950">
      {/* Left panel (hidden on mobile) */}
      <div className="hidden lg:flex flex-col justify-between bg-[#0F2942] p-12 text-white relative overflow-hidden">
        <div className="tricolor-bar absolute top-0 left-0 right-0" />
        <img
          src="https://customer-assets-wrfwihn1.emergentagent.net/job_ulb-tax-platform/artifacts/ki5z73xv_pmidc_main.webp"
          alt="Municipal office building"
          className="absolute inset-0 w-full h-full object-cover opacity-25 pointer-events-none select-none"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F2942] via-[#0F2942]/92 to-[#0F2942]/55 pointer-events-none" />
        <div className="tricolor-bar absolute top-0 left-0 right-0" />
        <Link to="/" className="flex items-center gap-3 relative" data-testid="login-brand">
          <PunjabEmblem size={44} className="bg-white/95 rounded-full p-0.5" />
          <div>
            <div className="font-heading font-semibold">PULB DRMS</div>
            <div className="text-[10px] uppercase tracking-widest opacity-70">
              Municipal Revenue Platform
            </div>
          </div>
        </Link>
        <div>
          <h2 className="text-4xl font-heading font-bold leading-tight">
            One unified digital gateway for<br />
            <span className="text-[#E8702A]">every Urban Local Body.</span>
          </h2>
          <p className="mt-6 text-sm text-white/70 max-w-md leading-relaxed">
          Role-based dashboards for officers, and self-service tools for citizens.
          </p>
        </div>
        <div className="text-xs text-white/60">© {new Date().getFullYear()} PULB Digital Revenue Management System</div>
      </div>

      {/* Right panel (form) */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <PunjabEmblem size={40} />
              <div className="font-heading font-semibold">PULB DRMS</div>
            </Link>
          </div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 dark:text-white">Sign in</h1>
          <p className="mt-2 text-sm text-slate-500">Access your PULB DRMS account.</p>

          <Tabs value={tab} onValueChange={setTab} className="mt-8">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="password" data-testid="tab-password-login">Password</TabsTrigger>
              <TabsTrigger value="otp" data-testid="tab-otp-login">OTP Login</TabsTrigger>
            </TabsList>

            <TabsContent value="password" className="mt-6">
              <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="input-email" autoComplete="email" />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link to="/forgot-password" className="text-xs text-[#0F2942] dark:text-slate-300 hover:underline" data-testid="link-forgot">
                      Forgot password?
                    </Link>
                  </div>
                  <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="input-password" autoComplete="current-password" />
                </div>
                <div>
                  <Label>Captcha</Label>
                  <div className="flex gap-2 items-center">
                    <div className="px-3 py-2 rounded-md bg-slate-100 dark:bg-slate-800 border font-mono text-sm flex-1 select-none" data-testid="captcha-question">{captchaQ || "…"}</div>
                    <Input type="text" required value={captchaA} onChange={(e) => setCaptchaA(e.target.value)} className="w-24" placeholder="Answer" data-testid="input-captcha" />
                    <Button type="button" variant="ghost" size="icon" onClick={refreshCaptcha} data-testid="btn-refresh-captcha">
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {error && <div className="text-sm text-red-600" data-testid="login-error">{error}</div>}
                <Button type="submit" className="w-full bg-[#0F2942] hover:bg-[#1E3A5F]" disabled={loading} data-testid="btn-login-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="otp" className="mt-6">
              <form onSubmit={verifyOtpSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="otp-email">Registered email</Label>
                  <Input id="otp-email" type="email" required value={otpEmail} onChange={(e) => setOtpEmail(e.target.value)} data-testid="input-otp-email" />
                </div>
                {!otpSent ? (
                  <Button type="button" onClick={requestOtp} className="w-full bg-[#E8702A] hover:bg-[#d95f1c]" disabled={otpLoading || !otpEmail} data-testid="btn-request-otp">
                    {otpLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send OTP"}
                  </Button>
                ) : (
                  <>
                    <div>
                      <Label htmlFor="otp">6-digit OTP</Label>
                      <Input id="otp" type="text" required maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value)} data-testid="input-otp" />
                      {otpDebug && <div className="mt-1 text-xs text-slate-500">Dev OTP: <span className="font-mono">{otpDebug}</span></div>}
                    </div>
                    <Button type="submit" className="w-full bg-[#0F2942]" disabled={otpLoading} data-testid="btn-verify-otp">
                      {otpLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & Sign in"}
                    </Button>
                    <button type="button" onClick={requestOtp} className="text-xs text-slate-500 hover:underline">Resend OTP</button>
                  </>
                )}
              </form>
            </TabsContent>
          </Tabs>

          <p className="mt-6 text-sm text-slate-500">
            New citizen?{" "}
            <Link to="/register" className="text-[#0F2942] dark:text-slate-200 font-semibold hover:underline" data-testid="link-register">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
